/**
 * Token Manager with TTL (30 days) support
 * Manages authentication token persistence in localStorage
 */
class TokenManager {
    constructor() {
        this.TOKEN_KEY = 'token';
        this.TOKEN_TIMESTAMP_KEY = 'token_timestamp'; // Key to store timestamp of when token was saved
        this.TOKEN_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    }

    /**
     * Saves token in localStorage with current timestamp
     * @param {string} token - Token to save
     */
    saveToken(token) {
        if (!token) {
            console.warn('TokenManager: Attempted to save empty token');
            return;
        }

        try {
            window.localStorage.setItem(this.TOKEN_KEY, token);
            window.localStorage.setItem(this.TOKEN_TIMESTAMP_KEY, Date.now().toString());
            console.log('TokenManager: Token saved successfully');
        } catch (err) {
            console.error('TokenManager: Failed to save token', err);
        }
    }

    /**
     * Gets token from localStorage if not expired
     * @returns {string|null} - Token or null if expired/not found
     */
    getToken() {
        try {
            const token = window.localStorage.getItem(this.TOKEN_KEY);
            const timestamp = window.localStorage.getItem(this.TOKEN_TIMESTAMP_KEY);

            if (!token || !timestamp) {
                return null;
            }

            const now = Date.now();
            const tokenAge = now - parseInt(timestamp, 10);

            // Check if token expired
            if (tokenAge > this.TOKEN_TTL) {
                console.log('TokenManager: Token expired, removing');
                this.removeToken();
                return null;
            }

            console.log(`TokenManager: Token retrieved, age: ${Math.floor(tokenAge / (24 * 60 * 60 * 1000))} days`);
            return token;
        } catch (err) {
            console.error('TokenManager: Failed to get token', err);
            return null;
        }
    }

    /**
     * Removes token from localStorage
     */
    removeToken() {
        try {
            window.localStorage.removeItem(this.TOKEN_KEY);
            window.localStorage.removeItem(this.TOKEN_TIMESTAMP_KEY);
            console.log('TokenManager: Token removed');
        } catch (err) {
            console.error('TokenManager: Failed to remove token', err);
        }
    }

    /**
     * Refreshes token timestamp (extends session)
     */
    refreshToken() {
        try {
            const token = window.localStorage.getItem(this.TOKEN_KEY);
            if (token) {
                window.localStorage.setItem(this.TOKEN_TIMESTAMP_KEY, Date.now().toString());
                console.log('TokenManager: Token timestamp refreshed');
            }
        } catch (err) {
            console.error('TokenManager: Failed to refresh token', err);
        }
    }

    /**
     * Checks if token exists and is valid
     * @returns {boolean}
     */
    hasValidToken() {
        return this.getToken() !== null;
    }
}

export default new TokenManager();
